/**
 * Positions a portaled submenu using fixed (viewport) coordinates relative to its anchor element.
 *
 * The submenu is rendered via React portal to document.body, so it has no parent-DOM
 * relationship with the option it belongs to. The anchor is passed as a ref object (not its
 * current value) because on first mount React fires the portal's ref callback BEFORE the
 * option div's ref callback — so anchorRef.current is only populated by the time the
 * setTimeout(0) below runs.
 *
 * @param {HTMLElement} node - The submenu DOM node
 * @param {string} submenuKey - Unique key for the submenu (stored in submenuRefs)
 * @param {Object} submenuRefs - Ref object that stores submenu DOM references
 * @param {{ current: HTMLElement|null }} anchorRef - Ref to the option element
 * @param {{ position?: 'left'|'right'|'center', optionPosition?: 'left'|'right' }} [opts]
 */
export const handleSubmenuPositioning = (
  node,
  submenuKey,
  submenuRefs,
  anchorRef,
  { position, optionPosition } = {}
) => {
  const prevNode = submenuRefs.current[submenuKey];
  if (prevNode && prevNode !== node && prevNode.__submenuResizeObserver) {
    prevNode.__submenuResizeObserver.disconnect();
    delete prevNode.__submenuResizeObserver;
  }

  submenuRefs.current[submenuKey] = node;

  if (!node) return;

  const reposition = () => {
    const anchorEl = anchorRef?.current;
    if (!node.isConnected || !anchorEl || !anchorEl.isConnected) return;

    try {
      const anchorRect = anchorEl.getBoundingClientRect();
      const submenuRect = node.getBoundingClientRect();

      if (!anchorRect || !submenuRect || submenuRect.height === 0) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 8;

      const submenuWidth = submenuRect.width;
      // scrollHeight includes absolutely-positioned contents (e.g. the filter
      // DateRangeContent) that don't contribute to the node's layout height.
      const submenuHeight = Math.max(submenuRect.height, node.scrollHeight);

      // ── HORIZONTAL ────────────────────────────────────────────────────────────
      const spaceRight = viewportWidth - anchorRect.right;
      const spaceLeft = anchorRect.left;
      const fitsOnRight = spaceRight >= submenuWidth;
      const fitsOnLeft = spaceLeft >= submenuWidth;

      const prefersLeft = optionPosition === 'left' || (!optionPosition && position === 'left');

      let placeOnRight;
      if (prefersLeft) {
        placeOnRight = !fitsOnLeft && fitsOnRight;
      } else {
        placeOnRight = fitsOnRight || !fitsOnLeft;
      }

      let left;
      if (placeOnRight) {
        // Prefer flush against the anchor's right edge, clamped to the viewport.
        left = Math.min(anchorRect.right, viewportWidth - submenuWidth - margin);
        if (left < margin) left = margin;
      } else {
        left = anchorRect.left - submenuWidth;
        if (left < margin) {
          // Neither side fits cleanly — clamp to viewport.
          left = Math.max(margin, viewportWidth - submenuWidth - margin);
        }
      }

      // ── VERTICAL ──────────────────────────────────────────────────────────────
      const spaceBelow = viewportHeight - anchorRect.top;
      const spaceAbove = anchorRect.bottom;
      const fitsBelow = spaceBelow >= submenuHeight;
      const fitsAbove = spaceAbove >= submenuHeight;

      let top;
      if (!fitsBelow && !fitsAbove) {
        // Doesn't fit either way — center vertically and cap height so it scrolls.
        top = Math.max(margin, (viewportHeight - submenuHeight) / 2);
      } else if (!fitsBelow && fitsAbove) {
        // Align bottom of submenu with bottom of anchor (submenu opens upward).
        top = anchorRect.bottom - submenuHeight;
      } else {
        // Default: align top of submenu slightly above anchor top so the first
        // option visually aligns with the parent option.
        top = anchorRect.top - 6;
      }

      if (top < margin) top = margin;

      node.style.position = 'fixed';
      node.style.top = `${top}px`;
      node.style.left = `${left}px`;
      node.style.right = 'unset';
      node.style.bottom = 'unset';
      // Cap at the viewport only — never at the measured height, since content can
      // load/grow after this runs (e.g. async filter options) and a frozen maxHeight
      // leaves the panel box (bg/shadow) shorter than its content.
      const viewportCap = viewportHeight - 2 * margin;
      node.style.maxHeight = `${viewportCap}px`;
      // When content exceeds the cap it must scroll; the explicit height matters for
      // absolutely-positioned contents, whose node otherwise stays near zero-height
      // and would clip everything once overflow stops being visible.
      if (node.scrollHeight > viewportCap) {
        node.style.height = `${viewportCap}px`;
        node.style.overflowY = 'auto';
      } else {
        node.style.height = '';
        node.style.overflowY = '';
      }
      node.style.visibility = '';
    } catch (error) {
      console.warn('Error during submenu positioning:', error);
    }
  };

  setTimeout(() => {
    reposition();

    // Re-run positioning when the submenu resizes (async content arriving,
    // search filtering the list) so it grows with its content and stays
    // within the viewport.
    if (!node.__submenuResizeObserver && typeof ResizeObserver !== 'undefined') {
      node.__submenuResizeObserver = new ResizeObserver(reposition);
      node.__submenuResizeObserver.observe(node);
    }
  }, 0);
};

/**
 * Generates a unique key for submenu based on its path
 * @param {string} parentPath - Path from parent levels
 * @param {number} level - Current nesting level
 * @param {number} index - Index within current level
 * @returns {string} Unique submenu key
 */
export const generateSubmenuKey = (parentPath, level, index) => {
  return parentPath ? `${parentPath}-${index}` : `${level}-${index}`;
};
